import type { CSSProperties } from "react";
import type {
  Alignment,
  Border,
  Borders,
  Cell,
  Color,
  Fill,
  FillPattern,
  Font,
  Worksheet,
} from "exceljs";
import ExcelJS, { ValueType } from "exceljs";
import * as XLSX from "xlsx";

// --- preview limits (grid + main-thread size gate) ---

/** Grid preview limits (also enforced when building rows in the worker). */
export const MAX_PREVIEW_ROWS = 2000;
/** Hard cap on rendered columns (Excel ranges can span thousands of columns). */
export const MAX_PREVIEW_COLS = 512;
/** Backstop so row × column preview cannot explode the DOM. */
export const MAX_PREVIEW_CELLS = 250_000;

/** Reject workbooks larger than this before parse (parse cost ~ scales with size). */
export const MAX_WORKBOOK_BYTES = 40 * 1024 * 1024;

// --- grid model ---

export type CellModel = {
  text: string;
  style?: CSSProperties;
};

export type SheetGridModel = {
  rows: CellModel[][];
  truncated: boolean;
  columnsTruncated: boolean;
  fullColCount: number;
  colCount: number;
  colWidthsPx: (number | undefined)[];
  rowHeightsPx: (number | undefined)[];
  hasColWidths: boolean;
};

export type XlsxPreviewSheetModel = SheetGridModel & {
  name: string;
  baseTableTypography?: CSSProperties;
};

// --- CSS helpers (ExcelJS → preview styles) ---

function mergeCss(
  ...parts: Array<CSSProperties | undefined>
): CSSProperties | undefined {
  const o: CSSProperties = {};
  for (const p of parts) {
    if (p) Object.assign(o, p);
  }
  return Object.keys(o).length > 0 ? o : undefined;
}

function appendCssImportant(value: string): string {
  const t = value.trim();
  if (!t) return t;
  return /\s!important\s*$/i.test(t) ? t : `${t} !important`;
}

function quoteCssFontFamily(name: string): string {
  const t = name.trim();
  if (!t) return t;
  if (/^".*"$/.test(t)) return t;
  if (/[\s,()]/.test(t)) return `"${t.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
  return t;
}

function excelColorToCss(color?: Partial<Color>): string | undefined {
  const argb = color?.argb;
  if (!argb || typeof argb !== "string") return undefined;
  const hex = argb.length >= 8 ? argb.slice(2) : argb.slice(-6);
  if (!/^[0-9A-Fa-f]{6}$/.test(hex)) return undefined;
  return `#${hex}`;
}

function fontToCss(font?: Partial<Font>): CSSProperties {
  if (!font || typeof font !== "object") return {};
  const css: CSSProperties = {};
  if (typeof font.size === "number" && font.size > 0) {
    css.fontSize = `${font.size}pt`;
  }
  if (typeof font.name === "string" && font.name.trim()) {
    css.fontFamily = appendCssImportant(quoteCssFontFamily(font.name));
  }
  if (font.bold) css.fontWeight = 700;
  if (font.italic) css.fontStyle = "italic";
  const col = excelColorToCss(font.color);
  if (col) css.color = col;
  if (font.underline && font.underline !== "none") {
    css.textDecoration = "underline";
  }
  return css;
}

function fillToCss(fill?: Fill): CSSProperties {
  if (!fill || typeof fill !== "object") return {};
  if (fill.type !== "pattern") return {};
  const f = fill as FillPattern;
  const fg = excelColorToCss(f.fgColor);
  const bg = excelColorToCss(f.bgColor);
  const style: CSSProperties = {};
  const pattern = f.pattern;
  switch (pattern) {
    case "solid":
      if (fg) style.backgroundColor = fg;
      else if (bg) style.backgroundColor = bg;
      break;
    case "gray125":
      style.backgroundColor = fg || bg || "#e4e4e4";
      break;
    case "gray0625":
      style.backgroundColor = fg || bg || "#f2f2f2";
      break;
    case "darkGray":
    case "mediumGray":
    case "lightGray":
      if (fg) style.backgroundColor = fg;
      else if (bg) style.backgroundColor = bg;
      break;
    case "none":
      if (fg) style.backgroundColor = fg;
      else if (bg) style.backgroundColor = bg;
      break;
    default:
      if (fg) style.backgroundColor = fg;
      else if (bg) style.backgroundColor = bg;
      break;
  }
  return Object.keys(style).length > 0 ? style : {};
}

const BORDER_WIDTH: Record<string, string> = {
  hair: "1px",
  thin: "1px",
  medium: "2px",
  thick: "3px",
  double: "3px",
  dotted: "1px",
  dashed: "1px",
  dashDot: "1px",
  dashDotDot: "1px",
  slantDashDot: "1px",
  mediumDashed: "1px",
  mediumDashDotDot: "1px",
  mediumDashDot: "1px",
};

const BORDER_LINE: Record<string, string> = {
  hair: "solid",
  thin: "solid",
  medium: "solid",
  thick: "solid",
  double: "double",
  dotted: "dotted",
  dashed: "dashed",
  dashDot: "dashed",
  dashDotDot: "dashed",
  slantDashDot: "dashed",
  mediumDashed: "dashed",
  mediumDashDotDot: "dashed",
  mediumDashDot: "dashed",
};

function borderSideToCss(
  side: "left" | "right" | "top" | "bottom",
  b?: Partial<Border>,
): CSSProperties {
  if (!b?.style || (b.style as string) === "none") return {};
  const w = BORDER_WIDTH[b.style] || "1px";
  const line = BORDER_LINE[b.style] || "solid";
  const color = excelColorToCss(b.color) || "#000000";
  const v = appendCssImportant(`${w} ${line} ${color}`);
  switch (side) {
    case "left":
      return { borderLeft: v };
    case "right":
      return { borderRight: v };
    case "top":
      return { borderTop: v };
    case "bottom":
      return { borderBottom: v };
    default:
      return {};
  }
}

function bordersToCss(b?: Partial<Borders>): CSSProperties {
  if (!b) return {};
  return {
    ...borderSideToCss("left", b.left),
    ...borderSideToCss("right", b.right),
    ...borderSideToCss("top", b.top),
    ...borderSideToCss("bottom", b.bottom),
  };
}

function alignmentToCss(alignment?: Partial<Alignment>): CSSProperties {
  if (!alignment || typeof alignment !== "object") return {};
  const css: CSSProperties = {};
  const h = alignment.horizontal;
  if (h === "center" || h === "left" || h === "right" || h === "justify") {
    css.textAlign = appendCssImportant(h) as CSSProperties["textAlign"];
  } else if (h === "centerContinuous") {
    css.textAlign = appendCssImportant("center") as CSSProperties["textAlign"];
  } else if (h === "distributed" || h === "fill") {
    css.textAlign = appendCssImportant("justify") as CSSProperties["textAlign"];
  }
  const v = alignment.vertical;
  if (v === "top") css.verticalAlign = appendCssImportant("top");
  else if (v === "middle") css.verticalAlign = appendCssImportant("middle");
  else if (v === "bottom") css.verticalAlign = appendCssImportant("bottom");
  if (alignment.wrapText) {
    css.whiteSpace = "normal";
    css.wordBreak = "break-word";
  }
  const tr = alignment.textRotation;
  if (tr === "vertical") {
    css.writingMode = "vertical-rl";
    css.textOrientation = "mixed";
  } else if (typeof tr === "number" && tr !== 0) {
    const deg = tr > 90 ? tr - 90 : tr;
    css.transform = `rotate(-${deg}deg)`;
    css.transformOrigin = "center center";
  }
  return css;
}

function cellStyleToCss(cell: Cell): CSSProperties | undefined {
  const s = cell.style;
  if (!s || typeof s !== "object") return undefined;
  return mergeCss(
    fontToCss(s.font),
    alignmentToCss(s.alignment),
    fillToCss(s.fill),
    bordersToCss(s.border),
  );
}

/**
 * Merged regions: only the master cell holds the value; ExcelJS merge slaves use
 * `ValueType.Merge` and would otherwise repeat the master's text and paint the same
 * transforms in every `<td>` (heavy smearing in the preview).
 */
function stripMergeSlaveLayoutCss(
  style: CSSProperties | undefined,
): CSSProperties | undefined {
  if (!style) return undefined;
  const {
    transform: _t,
    transformOrigin: _to,
    writingMode: _wm,
    textOrientation: _tor,
    ...rest
  } = style;
  return Object.keys(rest).length > 0 ? rest : undefined;
}

/** First number-format segment (Excel uses `positive;negative;zero;text`). */
function numFmtPositiveSegment(numFmt?: string | null): string {
  if (!numFmt) return "";
  return numFmt.split(";")[0] || "";
}

function stripNumFmtDecorators(s: string): string {
  return s.replace(/\[\$[^\]]+\]/gi, "");
}

/** Collapse `mmddyyyy` → `mdy` to infer display order. */
function ymdLetterOrderFromNumFmt(numFmt: string): string {
  const cleaned = stripNumFmtDecorators(numFmt)
    .replace(/".*?"/g, "")
    .toLowerCase();
  let seq = "";
  for (const ch of cleaned) {
    if (ch === "y" || ch === "m" || ch === "d") seq += ch;
  }
  let collapsed = "";
  for (const ch of seq) {
    if (collapsed[collapsed.length - 1] !== ch) collapsed += ch;
  }
  return collapsed;
}

function dateFieldOrderFromNumFmt(numFmt: string): "mdy" | "dmy" | "ymd" {
  const order = ymdLetterOrderFromNumFmt(numFmt);
  if (!order) return "mdy";
  if (order[0] === "y") return "ymd";
  if (order[0] === "d") return "dmy";
  return "mdy";
}

function numFmtHasNamedMonth(numFmt: string): boolean {
  return /m{3,4}/i.test(numFmtPositiveSegment(numFmt));
}

function numFmtSuggestsTime(numFmt: string): boolean {
  const s = stripNumFmtDecorators(numFmtPositiveSegment(numFmt)).toLowerCase();
  if (/\[h\]/.test(s)) return false;
  return /\b(h{1,2}|am\/pm)\b/.test(s) || /:mm/.test(s) || /\bss\b/.test(s);
}

function excelSerialToDate(serial: number, date1904: boolean): Date {
  const epochUtc = date1904
    ? Date.UTC(1904, 0, 1)
    : Date.UTC(1899, 11, 30);
  return new Date(epochUtc + Math.round(serial * 86400000));
}

function isLikelyDateNumberFormat(numFmt?: string | null): boolean {
  if (!numFmt) return false;
  const s = stripNumFmtDecorators(numFmtPositiveSegment(numFmt)).toLowerCase();
  const hasYear = /y{1,4}/.test(s);
  const hasDayAndMonth = /d/.test(s) && /m/.test(s);
  if (!(hasYear || hasDayAndMonth)) return false;
  if (/^0+%?$|0\.00|#,##0/.test(s.trim())) return false;
  return true;
}

/**
 * Format dates for HTML preview. ExcelJS `cell.text` uses `Date.toString()` for date
 * values, which is not what Excel shows; we approximate Excel using `numFmt` order.
 */
function formatExcelDateForPreview(
  date: Date,
  numFmt?: string | null | undefined,
): string {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

  const seg = numFmtPositiveSegment(numFmt);
  const stripped = stripNumFmtDecorators(seg);

  if (numFmtHasNamedMonth(numFmt || "")) {
    const longM = /m{4}/i.test(seg);
    return date.toLocaleDateString(undefined, {
      day: "numeric",
      month: longM ? "long" : "short",
      year: "numeric",
    });
  }

  if (numFmtSuggestsTime(stripped)) {
    const hour12 = /am\/pm/i.test(seg);
    const withSeconds = /ss/.test(stripped.toLowerCase());
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: withSeconds ? "2-digit" : undefined,
      hour12,
    }).format(date);
  }

  const order = dateFieldOrderFromNumFmt(stripped);
  const sep = stripped.includes("-") && !stripped.includes("/") ? "-" : "/";
  const y = date.getFullYear();
  const m = date.getMonth() + 1;
  const d = date.getDate();
  const p2 = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  if (order === "ymd") return `${y}${sep}${p2(m)}${sep}${p2(d)}`;
  if (order === "dmy") return `${p2(d)}${sep}${p2(m)}${sep}${y}`;
  return `${p2(m)}${sep}${p2(d)}${sep}${y}`;
}

function cellDisplayText(cell: Cell): string {
  if (cell.type === ValueType.Merge) {
    return "";
  }

  if (cell.type === ValueType.Date && cell.value instanceof Date) {
    return formatExcelDateForPreview(cell.value, cell.numFmt);
  }

  if (cell.type === ValueType.Formula && cell.result instanceof Date) {
    return formatExcelDateForPreview(cell.result, cell.numFmt);
  }

  const rawValue = cell.value;
  if (typeof rawValue === "number" && isLikelyDateNumberFormat(cell.numFmt)) {
    const date1904 = !!cell.workbook?.properties?.date1904;
    return formatExcelDateForPreview(
      excelSerialToDate(rawValue, date1904),
      cell.numFmt,
    );
  }

  const c = cell;
  const t = c.text;
  if (t != null && String(t).length > 0) return String(t);
  const v = c.value;
  if (v === null || v === undefined) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
    return String(v);
  }
  if (v instanceof Date) {
    return formatExcelDateForPreview(v, cell.numFmt);
  }
  if (typeof v === "object" && "richText" in v && Array.isArray((v as { richText: unknown }).richText)) {
    return (v as { richText: { text: string }[] }).richText.map((r) => r.text).join("");
  }
  if (typeof v === "object" && "text" in v && typeof (v as { text: string }).text === "string") {
    return (v as { text: string }).text;
  }
  if (typeof v === "object" && "result" in v) {
    const r = (v as { result?: unknown }).result;
    if (r instanceof Date) {
      return formatExcelDateForPreview(r, cell.numFmt);
    }
    if (r != null && r !== "") return String(r);
  }
  return "";
}

function colWidthPx(ws: Worksheet, col1: number): number | undefined {
  const col = ws.getColumn(col1);
  if (!col || col.hidden) return undefined;
  const w = col.width;
  if (typeof w === "number" && w > 0) {
    return Math.max(8, Math.round(w * 7 + 5));
  }
  return undefined;
}

function rowHeightPx(ws: Worksheet, row1: number): number | undefined {
  const row = ws.findRow(row1);
  if (!row || row.hidden) return undefined;
  const h = row.height;
  if (typeof h === "number" && h > 0) {
    return Math.round((h * 96) / 72);
  }
  return undefined;
}

export function sheetToCellGrid(ws: Worksheet): SheetGridModel {
  const dim = ws.dimensions;
  const rTop = dim.top;
  const cLeft = dim.left;
  const rBottom = dim.bottom;
  const cRight = dim.right;

  if (rBottom < rTop || cRight < cLeft) {
    return {
      rows: [],
      truncated: false,
      columnsTruncated: false,
      fullColCount: 1,
      colCount: 1,
      colWidthsPx: [],
      rowHeightsPx: [],
      hasColWidths: false,
    };
  }

  const fullColCount = Math.max(1, cRight - cLeft + 1);
  const maxR = Math.min(rBottom, rTop + MAX_PREVIEW_ROWS - 1);
  const truncated = rBottom > maxR;
  const nRows = maxR - rTop + 1;
  const maxColsByCellBudget = Math.max(
    1,
    Math.floor(MAX_PREVIEW_CELLS / Math.max(1, nRows)),
  );
  const maxVisibleCols = Math.min(MAX_PREVIEW_COLS, maxColsByCellBudget);
  const maxC = Math.min(cRight, cLeft + maxVisibleCols - 1);
  const columnsTruncated = cRight > maxC;
  const colCount = Math.max(1, maxC - cLeft + 1);

  const rows: CellModel[][] = [];
  for (let r = rTop; r <= maxR; r++) {
    const row: CellModel[] = [];
    for (let c = cLeft; c <= maxC; c++) {
      const cell = ws.getRow(r).getCell(c);
      const text = cellDisplayText(cell);
      const styleSource = cell.type === ValueType.Merge ? cell.master : cell;
      let style = cellStyleToCss(styleSource);
      if (cell.type === ValueType.Merge) {
        style = stripMergeSlaveLayoutCss(style);
      }
      row.push({ text, style });
    }
    rows.push(row);
  }

  const colWidthsPx: (number | undefined)[] = [];
  for (let i = 0; i < colCount; i++) {
    colWidthsPx.push(colWidthPx(ws, cLeft + i));
  }
  const rowHeightsPx: (number | undefined)[] = [];
  for (let i = 0; i < rows.length; i++) {
    rowHeightsPx.push(rowHeightPx(ws, rTop + i));
  }
  const hasColWidths = colWidthsPx.some((w) => w != null);

  return {
    rows,
    truncated,
    columnsTruncated,
    fullColCount,
    colCount,
    colWidthsPx,
    rowHeightsPx,
    hasColWidths,
  };
}

/** Load buffer into a workbook (browser-safe: avoids requiring Node `Buffer`). */
async function loadWorkbookFromArrayBuffer(buffer: ArrayBuffer): Promise<ExcelJS.Workbook> {
  const wb = new ExcelJS.Workbook();
  const u8 = new Uint8Array(buffer);
  // exceljs typings use Node `Buffer`; browser passes Uint8Array / ArrayBuffer view.
  await wb.xlsx.load(u8 as never);
  return wb;
}

/** Detect legacy .xls (BIFF/Compound Document) by its magic bytes D0 CF 11 E0. */
function isXlsBinaryBuffer(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < 8) return false;
  const b = new Uint8Array(buffer, 0, 8);
  return b[0] === 0xd0 && b[1] === 0xcf && b[2] === 0x11 && b[3] === 0xe0 &&
         b[4] === 0xa1 && b[5] === 0xb1 && b[6] === 0x1a && b[7] === 0xe1;
}

type XlsBorderSide = { style?: string; color?: { rgb?: string } };
type XlsCellStyle = {
  border?: {
    left?: XlsBorderSide;
    right?: XlsBorderSide;
    top?: XlsBorderSide;
    bottom?: XlsBorderSide;
  };
};

function xlsBorderSideToCss(
  side: "left" | "right" | "top" | "bottom",
  b: XlsBorderSide | undefined,
): CSSProperties {
  if (!b?.style || b.style === "none") return {};
  const w = BORDER_WIDTH[b.style] ?? "1px";
  const line = BORDER_LINE[b.style] ?? "solid";
  const color = b.color?.rgb ? `#${b.color.rgb.slice(-6)}` : "#000000";
  const v = `${w} ${line} ${color}`;
  switch (side) {
    case "left":   return { borderLeft: v };
    case "right":  return { borderRight: v };
    case "top":    return { borderTop: v };
    case "bottom": return { borderBottom: v };
  }
}

function xlsCellStyleToCss(s: XlsCellStyle | undefined): CSSProperties | undefined {
  if (!s?.border) return undefined;
  const css: CSSProperties = {
    ...xlsBorderSideToCss("left",   s.border.left),
    ...xlsBorderSideToCss("right",  s.border.right),
    ...xlsBorderSideToCss("top",    s.border.top),
    ...xlsBorderSideToCss("bottom", s.border.bottom),
  };
  return Object.keys(css).length > 0 ? css : undefined;
}

/** Parse a .xls file using SheetJS (ExcelJS only handles .xlsx). */
function parseXlsBufferToPreviewSheets(buffer: ArrayBuffer): XlsxPreviewSheetModel[] {
  const wb = XLSX.read(new Uint8Array(buffer), { type: "array", cellDates: true, cellStyles: true });
  const out: XlsxPreviewSheetModel[] = [];

  for (const sheetName of wb.SheetNames) {
    const ws = wb.Sheets[sheetName];
    const ref = ws["!ref"];
    if (!ref) {
      out.push({ name: sheetName, rows: [], truncated: false, columnsTruncated: false, fullColCount: 1, colCount: 1, colWidthsPx: [], rowHeightsPx: [], hasColWidths: false, baseTableTypography: {} });
      continue;
    }

    const range = XLSX.utils.decode_range(ref);
    const rTop = range.s.r;
    const cLeft = range.s.c;
    const rBottom = Math.min(range.e.r, rTop + MAX_PREVIEW_ROWS - 1);
    const fullColCount = Math.max(1, range.e.c - cLeft + 1);
    const maxC = Math.min(range.e.c, cLeft + MAX_PREVIEW_COLS - 1);
    const truncated = range.e.r > rBottom;
    const columnsTruncated = range.e.c > maxC;
    const colCount = Math.max(1, maxC - cLeft + 1);

    const rows: CellModel[][] = [];
    for (let r = rTop; r <= rBottom; r++) {
      const row: CellModel[] = [];
      for (let c = cLeft; c <= maxC; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        const cell: XLSX.CellObject | undefined = ws[addr];
        let text = "";
        if (cell) {
          if (cell.t === "d" && cell.v instanceof Date) {
            text = cell.v.toLocaleDateString();
          } else if (cell.w != null) {
            text = String(cell.w);
          } else if (cell.v != null) {
            text = String(cell.v);
          }
        }
        const style = xlsCellStyleToCss(cell?.s as XlsCellStyle | undefined);
        row.push({ text, ...(style ? { style } : {}) });
      }
      rows.push(row);
    }

    out.push({
      name: sheetName,
      rows,
      truncated,
      columnsTruncated,
      fullColCount,
      colCount,
      colWidthsPx: Array(colCount).fill(undefined),
      rowHeightsPx: Array(rows.length).fill(undefined),
      hasColWidths: false,
      baseTableTypography: {},
    });
  }

  return out;
}

/**
 * Full parse + grid build for the XLSX preview worker (keeps workbook parse off the main thread).
 */
export async function parseWorkbookArrayBufferToPreviewSheets(
  buffer: ArrayBuffer,
): Promise<XlsxPreviewSheetModel[]> {
  if (isXlsBinaryBuffer(buffer)) {
    return parseXlsBufferToPreviewSheets(buffer);
  }
  const wb = await loadWorkbookFromArrayBuffer(buffer);
  const out: XlsxPreviewSheetModel[] = [];
  wb.eachSheet((ws) => {
    const grid = sheetToCellGrid(ws);
    out.push({
      name: ws.name || `Sheet ${out.length + 1}`,
      ...grid,
      baseTableTypography: {},
    });
  });
  return out;
}
